import { ReactElement, useRef, useEffect, useState } from "react"
import styled from "styled-components"
import { breakpoints, videoBackground, zIndex } from "../lib/theme"

export interface VideoBackgroundProps {
  videoSrc: string
  posterSrc?: string
  blur?: number
  opacity?: number
  className?: string
}

export default function VideoBackground({
  videoSrc,
  posterSrc,
  blur = videoBackground.blur.default,
  opacity = videoBackground.opacity.default,
  className,
}: VideoBackgroundProps): ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [scrollOpacity, setScrollOpacity] = useState(opacity)
  const [isPortrait, setIsPortrait] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const [isIOS, setIsIOS] = useState(false)

  // Detect iOS device
  useEffect(() => {
    const detectIOS = () => {
      const userAgent = navigator.userAgent
      const isIOSDevice =
        /iPad|iPhone|iPod/.test(userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      setIsIOS(isIOSDevice)

      // Debug logging
      if (isIOSDevice) {
        console.log("iOS device detected, applying iOS-specific video handling")
      }
    }

    detectIOS()
  }, [])

  // Check if device is in portrait orientation
  useEffect(() => {
    const checkOrientation = () => {
      const portrait = window.matchMedia(breakpoints.portrait).matches
      setIsPortrait(portrait)
    }

    checkOrientation()
    window.addEventListener("resize", checkOrientation)
    window.addEventListener("orientationchange", checkOrientation)

    return () => {
      window.removeEventListener("resize", checkOrientation)
      window.removeEventListener("orientationchange", checkOrientation)
    }
  }, [])

  // Enhanced video loading and playback handling
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadedData = () => {
      setVideoLoaded(true)
      setVideoError(false)
    }

    const handleError = () => {
      setVideoError(true)
      setVideoLoaded(false)
    }

    const handleCanPlay = () => {
      // Force play attempt on iOS
      if (isIOS) {
        video.play().catch((error) => {
          console.warn("Video autoplay failed on iOS:", error)
          // Fallback to poster image
          setVideoError(true)
        })
      }
    }

    // Add event listeners
    video.addEventListener("loadeddata", handleLoadedData)
    video.addEventListener("error", handleError)
    video.addEventListener("canplay", handleCanPlay)

    // iOS-specific: Try to load and play video
    if (isIOS) {
      video.load()
    }

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData)
      video.removeEventListener("error", handleError)
      video.removeEventListener("canplay", handleCanPlay)
    }
  }, [isIOS])

  // Handle user interaction to trigger video playback on iOS
  useEffect(() => {
    const video = videoRef.current
    if (!video || !isIOS) return

    let hasTriggered = false

    const handleUserInteraction = () => {
      if (hasTriggered) return
      hasTriggered = true

      video.play().catch((error) => {
        console.warn("Video play failed after user interaction:", error)
        setVideoError(true)
      })
    }

    // Listen for any user interaction
    const events = ["touchstart", "touchend", "click", "scroll"]
    events.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, {
        once: true,
        passive: true,
      })
    })

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction)
      })
    }
  }, [isIOS])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleScroll = () => {
      const scrollY = window.scrollY

      // Use portrait-specific settings if in portrait mode
      const threshold = isPortrait
        ? videoBackground.scroll.portrait.threshold
        : videoBackground.scroll.threshold

      const fadeDistance = isPortrait
        ? videoBackground.scroll.portrait.fadeDistance
        : videoBackground.scroll.fadeDistance

      if (scrollY <= threshold) {
        setScrollOpacity(opacity)
      } else if (scrollY >= threshold + fadeDistance) {
        setScrollOpacity(0)
      } else {
        const progress = (scrollY - threshold) / fadeDistance
        setScrollOpacity(opacity * (1 - progress))
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [opacity, isPortrait])

  return (
    <VideoWrapper className={className} $opacity={scrollOpacity}>
      {/* Show poster image if video fails to load or on iOS with errors */}
      {(videoError || (!videoLoaded && posterSrc)) && (
        <PosterImage
          src={posterSrc}
          alt="Background"
          $blur={blur}
          $show={videoError || !videoLoaded}
        />
      )}

      <StyledVideo
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={posterSrc}
        $blur={blur}
        $show={!videoError}
        aria-hidden="true"
        // iOS-specific attributes
        webkit-playsinline="true"
        x5-playsinline="true"
        x5-video-player-type="h5"
        x5-video-player-fullscreen="true"
        x5-video-orientation="portraint">
        <source src={videoSrc} type="video/mp4" />
        {/* Fallback message for very old browsers */}
        Your browser does not support the video tag.
      </StyledVideo>
    </VideoWrapper>
  )
}

const VideoWrapper = styled.div<{ $opacity: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  z-index: ${zIndex.videoBackground};
  background: #000;

  opacity: ${(props) => props.$opacity};
  transition: opacity ${videoBackground.scroll.duration}s ease-out;

  /* Ensure no touch interference */
  pointer-events: none;
  touch-action: none;

  /* Fallback background image if everything else fails */
  background-image: url("bg-video/background-video-backup.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`

const PosterImage = styled.img<{ $blur: number; $show: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(1.2);
  min-width: 100%;
  min-height: 100%;

  filter: blur(${(props) => props.$blur}px);

  display: ${(props) => (props.$show ? "block" : "none")};
  z-index: 1;
`

const StyledVideo = styled.video<{ $blur: number; $show: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;

  filter: blur(${(props) => props.$blur}px);

  display: ${(props) => (props.$show ? "block" : "none")};
  z-index: 2;

  /* Ensure video covers full screen */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(1.2);
  min-width: 100%;
  min-height: 100%;

  /* Respect user preferences for reduced motion */
  @media ${breakpoints.reduceMotion} {
    display: none;
  }

  /* Responsive optimizations without hiding video */
  @media ${breakpoints.mobile} {
    /* Slightly optimized scale for mobile performance */
    transform: translate(-50%, -50%) scale(1.1);
  }

  @media ${breakpoints.tablet} {
    /* Optimized scale for tablets */
    transform: translate(-50%, -50%) scale(1.15);
  }

  /* iOS-specific optimizations */
  @media ${breakpoints.touchDevice} {
    /* Reduce blur on touch devices for better performance */
    filter: blur(${(props) => Math.max(props.$blur - 2, 0)}px);
  }
`
